import { useEffect, useMemo, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import {
  fetchAllCodeTables,
  fetchCodeTableByType,
  refreshCodeTables,
} from 'src/reducers/codeTableSlice';
import type { CodeValue, DropdownOption, CodeTableType } from 'src/types/codeTable';

// Re-export types and constants for convenience
export { CODE_TABLE_TYPES, STATUS_CODES } from 'src/types/codeTable';
export type { CodeValue, DropdownOption, CodeTableType } from 'src/types/codeTable';

interface UseCodeTablesReturn {
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;

  // Get raw values
  getValues: (typeCode: CodeTableType | string) => CodeValue[];

  // Dropdown helpers
  getDropdownOptions: (typeCode: CodeTableType | string) => DropdownOption[];
  getMenuItems: (typeCode: CodeTableType | string) => Array<{ key: number; value: number; label: string }>;

  // Lookup helpers
  findById: (typeCode: CodeTableType | string, id: number) => CodeValue | undefined;
  findByTypeCode: (typeCode: CodeTableType | string, valueTypeCode: string) => CodeValue | undefined;
  getNameById: (typeCode: CodeTableType | string, id: number) => string;

  // Actions
  refresh: () => void;
}

/**
 * Main hook for accessing code tables
 *
 * @param typeCodes - Optional array of type codes this component needs.
 *                    When specified, isLoading only reflects these types.
 *                    Self-documenting: shows what code tables a component uses.
 *
 * @example
 * // Specify which code tables you need (recommended)
 * const { getValues, isLoading } = useCodeTables([
 *   CODE_TABLE_TYPES.DF_LOGIN_TYPE,
 *   CODE_TABLE_TYPES.DF_MODULE,
 * ]);
 * // isLoading = false once DF_LOGIN_TYPE and DF_MODULE are cached
 *
 * // Or fetch all (backward compatible)
 * const { getMenuItems, findByTypeCode, isLoading } = useCodeTables();
 *
 * // Get menu items for a dropdown
 * const userTypes = getMenuItems(CODE_TABLE_TYPES.USER_TYPE);
 *
 * // Find a specific status
 * const approvedStatus = findByTypeCode(CODE_TABLE_TYPES.LEAVE_STATUS, STATUS_CODES.APPROVED);
 */
export function useCodeTables(typeCodes?: (CodeTableType | string)[]): UseCodeTablesReturn {
  const dispatch = useAppDispatch();
  const {
    byTypeCode,
    loading,
    isFullyLoaded,
    error,
  } = useAppSelector((state) => state.codeTables);

  // Auto-fetch on first use (always fetches all - API returns full payload)
  // Don't retry if there's an error (e.g., 403 Forbidden) — prevents infinite loop
  useEffect(() => {
    if (!isFullyLoaded && !loading && !error) {
      dispatch(fetchAllCodeTables());
    }
  }, [dispatch, isFullyLoaded, loading, error]);

  // Warn about missing code tables after initial load completes
  useEffect(() => {
    if (isFullyLoaded && typeCodes && typeCodes.length > 0) {
      const missingTypes = typeCodes.filter(tc => !byTypeCode[tc]);
      if (missingTypes.length > 0) {
        console.warn(
          `[useCodeTables] The following code table types are not configured in the database: ${missingTypes.join(', ')}. ` +
          `These dropdowns will be empty. To fix this, add these code tables via the admin panel or database migration.`
        );
      }
    }
  }, [isFullyLoaded, typeCodes, byTypeCode]);

  // Smart loading state - only reflects requested types when specified
  // BUT: if isFullyLoaded, stop waiting even if some types are missing (they don't exist)
  const isLoadingRequested = useMemo(() => {
    if (typeCodes && typeCodes.length > 0) {
      // If fully loaded, we're done - missing types don't exist in DB
      if (isFullyLoaded) {
        return false;
      }
      // Still loading if global loading OR any requested type is missing
      return loading || typeCodes.some(tc => !byTypeCode[tc]);
    }
    return loading;
  }, [typeCodes, loading, byTypeCode, isFullyLoaded]);

  // Smart loaded state - checks only requested types when specified
  // BUT: if isFullyLoaded, consider it loaded even if some types are missing
  const isLoadedRequested = useMemo(() => {
    if (typeCodes && typeCodes.length > 0) {
      // If fully loaded, we're done regardless of missing types
      if (isFullyLoaded) {
        return true;
      }
      return typeCodes.every(tc => !!byTypeCode[tc]);
    }
    return isFullyLoaded;
  }, [typeCodes, byTypeCode, isFullyLoaded]);

  // Get raw values array
  const getValues = useCallback(
    (typeCode: string): CodeValue[] => {
      return byTypeCode[typeCode] || [];
    },
    [byTypeCode]
  );

  // Convert to dropdown options format
  const getDropdownOptions = useCallback(
    (typeCode: string): DropdownOption[] => {
      return (byTypeCode[typeCode] || []).map((item) => ({
        value: item.id,
        label: item.name,
        typeCode: item.typeCode,
      }));
    },
    [byTypeCode]
  );

  // Get array ready for MUI MenuItem mapping
  const getMenuItems = useCallback(
    (typeCode: string) => {
      return (byTypeCode[typeCode] || []).map((item) => ({
        key: item.id,
        value: item.id,
        label: item.name,
      }));
    },
    [byTypeCode]
  );

  // Find value by ID
  const findById = useCallback(
    (typeCode: string, id: number): CodeValue | undefined => {
      return byTypeCode[typeCode]?.find((item) => item.id === id);
    },
    [byTypeCode]
  );

  // Find value by nested typeCode (e.g., find 'approved_ref' in 'leave_status_ref')
  const findByTypeCode = useCallback(
    (typeCode: string, valueTypeCode: string): CodeValue | undefined => {
      return byTypeCode[typeCode]?.find((item) => item.typeCode === valueTypeCode);
    },
    [byTypeCode]
  );

  // Get display name by ID
  const getNameById = useCallback(
    (typeCode: string, id: number): string => {
      return byTypeCode[typeCode]?.find((item) => item.id === id)?.name || '';
    },
    [byTypeCode]
  );

  // Force refresh
  const refresh = useCallback(() => {
    dispatch(refreshCodeTables());
  }, [dispatch]);

  return {
    isLoading: isLoadingRequested,
    isLoaded: isLoadedRequested,
    error,
    getValues,
    getDropdownOptions,
    getMenuItems,
    findById,
    findByTypeCode,
    getNameById,
    refresh,
  };
}

/**
 * Convenience hook for a single code table type
 *
 * @example
 * const { values, options, menuItems, isLoading } = useCodeValues(CODE_TABLE_TYPES.USER_TYPE);
 *
 * // Use with MUI Select
 * <TextField select>
 *   {menuItems.map(item => (
 *     <MenuItem key={item.key} value={item.value}>{item.label}</MenuItem>
 *   ))}
 * </TextField>
 */
export function useCodeValues(typeCode: CodeTableType | string) {
  const { getValues, getDropdownOptions, getMenuItems, isLoading, isLoaded, findById, findByTypeCode, getNameById } = useCodeTables();

  return useMemo(
    () => ({
      values: getValues(typeCode),
      options: getDropdownOptions(typeCode),
      menuItems: getMenuItems(typeCode),
      isLoading: !isLoaded || isLoading,
      // Helper functions scoped to this type
      findById: (id: number) => findById(typeCode, id),
      findByTypeCode: (valueTypeCode: string) => findByTypeCode(typeCode, valueTypeCode),
      getNameById: (id: number) => getNameById(typeCode, id),
    }),
    [typeCode, getValues, getDropdownOptions, getMenuItems, isLoading, isLoaded, findById, findByTypeCode, getNameById]
  );
}

/**
 * Hook with lazy loading for specific code table
 * Use when you need a code table that might not be pre-loaded
 *
 * @example
 * const { values, isLoading, isLoaded } = useCodeTableLazy('custom_type_ref');
 */
export function useCodeTableLazy(typeCode: CodeTableType | string) {
  const dispatch = useAppDispatch();
  const { byTypeCode, loadingTypeCodes, isFullyLoaded } = useAppSelector(
    (state) => state.codeTables
  );

  useEffect(() => {
    // If not fully loaded and this specific type isn't loaded, fetch it
    if (!isFullyLoaded && !byTypeCode[typeCode] && !loadingTypeCodes.includes(typeCode)) {
      dispatch(fetchCodeTableByType(typeCode));
    }
  }, [dispatch, typeCode, byTypeCode, isFullyLoaded, loadingTypeCodes]);

  return {
    values: byTypeCode[typeCode] || [],
    isLoading: loadingTypeCodes.includes(typeCode),
    isLoaded: !!byTypeCode[typeCode],
  };
}

/**
 * Hook that returns props for MUI Autocomplete
 *
 * @example
 * const autocompleteProps = useCodeTableAutocompleteProps(CODE_TABLE_TYPES.USER_TYPE);
 *
 * <Autocomplete {...autocompleteProps} renderInput={...} />
 */
export function useCodeTableAutocompleteProps(typeCode: CodeTableType | string) {
  const { options, isLoading } = useCodeValues(typeCode);

  return useMemo(
    () => ({
      options,
      loading: isLoading,
      getOptionLabel: (option: DropdownOption | null) => option?.label || '',
      isOptionEqualToValue: (option: DropdownOption | null, value: DropdownOption | null) =>
        option?.value === value?.value,
    }),
    [options, isLoading]
  );
}
